import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Footer as FooterUI } from '../../ui/Footer/Footer.js';
import { dataMapper } from './dataMapper.js';
import { getLabelsForFields } from '../../utils/labelHelper';
import CustomerContactModal from '../CustomerContactModal';
import useAccessoryItems from '../../hooks/useAccessoryItems';
import useProvidersList from '../../hooks/useProvidersList';
import useProfessionalsList from '../../hooks/useProfessionalsList';
import useProfessionalDetails from '../../hooks/useProfessionalDetails.js';
import {
  routes,
  ACCESS_ROLES,
  envUrlClinicianManagment,
} from '../../config.js';
import useMcpEvents from '../../hooks/useMcpEvents';
import useLiveSessionCheck from '../../hooks/useLiveSessionCheck';
import userService from '../../services/userService.js';
import useProfessionalAccessRoles from '../../hooks/useProfessionalAccessRoles.js';
import { initFederatedHost } from '../ScheduleClinicianManagement';
import { useProviderDevices } from '../../hooks/useProviderDevices.js';
import { shouldUseNewClinicEquipment } from '../../utils/portalFeatures.js';
import providerService from '../../services/providerService.js';

const Footer = ({ rendering }) => {
  const { role } = userService.getUserDetails();
  const location = useLocation();
  const { pathname } = location;
  const [isPreloadingCMComplete, setIsPreloadingCMComplete] =
    useState(false);
  // Check if current page is team-members
  const isTeamMembersPage = /(^|\/)team-members(\/|$)/.test(pathname);
  // Check if current page is home page
  const isHomePage = /(^|\/)home(\/|$)/.test(pathname);

  // Preload Module Federation remote
  useEffect(() => {
    const preloadRemoteModules = async () => {
      try {
        const clinicManagementConfig = {
          enabled: true,
          scheduleCMScriptPath: `${envUrlClinicianManagment}/static/chunks/remoteEntry.js`,
          remoteModulePath: 'clinicianManagement/ClinicianApp',
        };

        const host = initFederatedHost(clinicManagementConfig);
        await host.loadRemote(
          clinicManagementConfig.remoteModulePath,
        );
        setIsPreloadingCMComplete(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('CM Remote module preloading failed:', error);
      }
    };
    if (!isPreloadingCMComplete && (isHomePage || isTeamMembersPage))
      preloadRemoteModules();
  }, []);

  // Fetch accessory items
  useAccessoryItems();

  // Fetch providers list
  useProvidersList();

  // Determine if we should fetch professionals
  const shouldFetchProfessionals =
    role !== ACCESS_ROLES.RESOURCES_AND_TRAINING;

  // Fetch professional list (for CM)
  useProfessionalsList({ enabled: shouldFetchProfessionals });
  useProfessionalDetails(
    userService.getUserDetails().professionalId,
    { enabled: true },
  );
  useProfessionalAccessRoles(
    userService.getUserDetails().professionalId,
    { enabled: true },
  );

  // Fetch clinic equipment list
  const currentProvider = providerService.getCurrentProvider();
  const shouldFetchProviderDevices =
    shouldFetchProfessionals &&
    shouldUseNewClinicEquipment(currentProvider);
  useProviderDevices({
    enabled: shouldFetchProviderDevices,
  });

  // Track MCP session status
  const { mcpLoaded } = useMcpEvents();
  const { showButton } = useLiveSessionCheck();

  // Need refs to track the latest values
  // in setInterval in useEffect
  const mcpLoadedRef = useRef(mcpLoaded);
  const showButtonRef = useRef(showButton);

  // keep refs up to date
  useEffect(() => {
    mcpLoadedRef.current = mcpLoaded;
    showButtonRef.current = showButton;
  }, [mcpLoaded, showButton]);

  const disableIframes =
    localStorage.getItem('disableIframes') === 'true';

  // Calls MCP every 3 mins to refresh cookie and keep session alive
  useEffect(() => {
    if (!disableIframes) {
      const intervalId = setInterval(() => {
        const keepAliveFrame = document.getElementById('keepAlive');
        if (keepAliveFrame) {
          if (!(showButtonRef.current && !mcpLoadedRef.current)) {
            // no button
            // change the src to load the keepAlive route
            // eslint-disable-next-line no-console
            console.log('[MCP Session Check] adding keepAlive');
            keepAliveFrame.src = routes.mcp.keepAlive;
          }
        }
      }, 180000);

      return () => clearInterval(intervalId);
    }

    return () => {};
  }, []);

  const [openModal, setOpenModal] = useState(false);

  const modalType = 'contactUs';
  const labels = getLabelsForFields(rendering.fields);
  const res = dataMapper(labels);

  useEffect(() => {
    document
      .getElementsByClassName('list-group-item')[0]
      .addEventListener('click', (event) => {
        event.preventDefault();
        setOpenModal(true);
      });
  }, []);

  const handleModalClosed = () => {
    setOpenModal(false);
  };

  return (
    <>
      {!disableIframes && (
        <iframe
          id="keepAlive"
          title="keepAlive"
          src="about:blank"
          hidden
        />
      )}

      <FooterUI config={res.config} labels={labels} />
      <CustomerContactModal
        openModal={openModal}
        modalType={modalType}
        labels={labels}
        res={res}
        onCloseClicked={handleModalClosed}
      />
    </>
  );
};

export default Footer;
